import React, { useState, FormEvent } from 'react';
import {
    Zap,
    Brain,
    Sprout,
    ShieldCheck,
    ChevronRight,
    Menu,
    X,
    ArrowRight,
    Users,
    Target
} from 'lucide-react';

// Woddy Logo Component - Represents a work buddy through connected circles forming a "W"
// Minimalist design with theme colors: slate-900 background, indigo accent
const WoddyLogo = ({ className = "w-10 h-10" }: { className?: string }) => (
    <svg
        viewBox="0 0 40 40"
        className={className}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        {/* Rounded square background - slate-900 to match theme */}
        <rect
            x="4"
            y="4"
            width="32"
            height="32"
            rx="8"
            fill="#1e293b"
        />

        {/* Stylized "W" formed by connected elements - represents work buddy collaboration */}
        {/* Left peak */}
        <path
            d="M11 28L14 16L17 22"
            stroke="white"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
        />

        {/* Center peak */}
        <path
            d="M17 22L20 16L23 22"
            stroke="white"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
        />

        {/* Right peak */}
        <path
            d="M23 22L26 16L29 28"
            stroke="white"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
        />

        {/* Connection accent at bottom - indigo accent representing collaboration */}
        <path
            d="M13 28L27 28"
            stroke="#6366f1"
            strokeWidth="3"
            strokeLinecap="round"
            opacity="0.9"
        />
    </svg>
);

type FormState = 'idle' | 'submitting' | 'success' | 'error';

const App = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [email, setEmail] = useState('');
    const [formState, setFormState] = useState<FormState>('idle');
    const [errorMessage, setErrorMessage] = useState('');

    const scrollToSection = (sectionId: string) => {
        const element = document.getElementById(sectionId);
        if (element) {
            const navHeight = 80; // Height of fixed navbar
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - navHeight;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
        // Close mobile menu if open
        setIsMenuOpen(false);
    };

    const openModal = () => {
        setIsModalOpen(true);
        setIsMenuOpen(false);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        if (formState === 'success') {
            setFormState('idle');
            setEmail('');
        }
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        // HTML5 validation
        const form = e.currentTarget;
        if (!form.checkValidity()) {
            return;
        }

        setFormState('submitting');
        setErrorMessage('');

        try {
            const formData = new URLSearchParams();
            formData.append('form-name', 'early-access');
            formData.append('email', email);
            formData.append('bot-field', ''); // Honeypot field (should be empty)

            const response = await fetch('/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: formData.toString(),
            });

            if (response.ok) {
                setFormState('success');
                setEmail('');
            } else {
                // Log the error for debugging
                console.error('Form submission failed:', response.status, response.statusText);
                throw new Error(`Submission failed: ${response.status}`);
            }
        } catch (error) {
            console.error('Form submission error:', error);
            setFormState('error');
            setErrorMessage('Something went wrong. Please try again or refresh the page.');
        }
    };

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
        if (formState === 'error') {
            setFormState('idle');
            setErrorMessage('');
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 text-slate-900 font-sans selection:bg-indigo-100 selection:text-indigo-900">
            {/* Navigation */}
            <nav className="fixed w-full z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between h-20 items-center">
                        <div className="flex items-center gap-2">
                            <WoddyLogo className="w-10 h-10" />
                            <span className="text-2xl font-bold tracking-tight text-slate-900">WODDY</span>
                        </div>

                        <div className="hidden md:flex items-center space-x-8">
                            <button onClick={() => scrollToSection('why')} className="text-sm font-medium text-slate-600 hover:text-indigo-600 transition-colors">Why Woddy</button>
                            <button onClick={() => scrollToSection('assistant')} className="text-sm font-medium text-slate-600 hover:text-indigo-600 transition-colors">Assistant</button>
                            <button onClick={() => scrollToSection('coach')} className="text-sm font-medium text-slate-600 hover:text-indigo-600 transition-colors">Coach</button>
                            <button onClick={openModal} className="bg-slate-900 text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-slate-800 transition-all shadow-sm">
                                Talk to Us
                            </button>
                        </div>

                        <div className="md:hidden">
                            <button onClick={() => setIsMenuOpen(!isMenuOpen)}>
                                {isMenuOpen ? <X /> : <Menu />}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Mobile Menu */}
                {isMenuOpen && (
                    <div className="md:hidden bg-white border-b border-slate-200 p-4 space-y-4">
                        <button onClick={() => scrollToSection('why')} className="block text-lg font-medium w-full text-left">Why Woddy</button>
                        <button onClick={() => scrollToSection('assistant')} className="block text-lg font-medium w-full text-left">Assistant</button>
                        <button onClick={() => scrollToSection('coach')} className="block text-lg font-medium w-full text-left">Coach</button>
                        <button onClick={openModal} className="w-full bg-indigo-600 text-white py-3 rounded-xl font-bold">Get Started</button>
                    </div>
                )}
            </nav>

            {/* Hero Section */}
            <section className="pt-32 pb-20 md:pt-48 md:pb-32 px-4">
                <div className="max-w-4xl mx-auto text-center">
                    <div className="inline-flex items-center gap-2 bg-indigo-50 text-indigo-700 px-4 py-1.5 rounded-full text-sm font-medium mb-8">
                        <ShieldCheck size={16} />
                        <span>Built for Real Work, Not Chaos</span>
                    </div>
                    <h1 className="text-5xl md:text-7xl font-extrabold text-slate-900 tracking-tight mb-6">
                        Your Work Buddy.
                    </h1>
                    <p className="text-xl md:text-2xl text-slate-600 mb-10 leading-relaxed max-w-2xl mx-auto">
                        Move faster. Keep knowledge. Grow through real work. Reduce operational delays without giving up control.
                    </p>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
                        <button
                            onClick={() => scrollToSection('assistant')}
                            className="w-full sm:w-auto bg-slate-900 text-white px-8 py-4 rounded-full text-lg font-bold hover:scale-105 transition-transform flex items-center justify-center gap-2 group"
                        >
                            Explore Assistant
                            <ArrowRight className="group-hover:translate-x-1 transition-transform" />
                        </button>
                        <button
                            onClick={() => scrollToSection('coach')}
                            className="w-full sm:w-auto bg-white border-2 border-slate-200 text-slate-900 px-8 py-4 rounded-full text-lg font-bold hover:border-slate-300 transition-all"
                        >
                            Explore Coach
                        </button>
                    </div>
                </div>
            </section>

            {/* Problem Section */}
            <section id="why" className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-2 gap-16 items-center">
                        <div>
                            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-slate-900 leading-tight">
                                Work doesn’t fail because people aren’t capable.
                            </h2>
                            <p className="text-lg text-slate-600 mb-8">
                                It fails because information is hard to access, the right person isn't available, and context gets lost over time. Woddy is built to fix this - without creating risk.
                            </p>
                            <div className="space-y-4">
                                {[
                                    { title: "Move work faster", desc: "Get answers and prepare work without uncontrolled automation.", icon: <Zap className="text-amber-500" /> },
                                    { title: "Keep knowledge", desc: "Important context stays accessible even when roles change.", icon: <Brain className="text-indigo-500" /> },
                                    { title: "Grow through work", desc: "Progress grounded in what you actually worked on.", icon: <Sprout className="text-emerald-500" /> }
                                ].map((item, i) => (
                                    <div key={i} className="flex gap-4 p-4 rounded-2xl hover:bg-slate-50 transition-colors">
                                        <div className="mt-1">{item.icon}</div>
                                        <div>
                                            <h4 className="font-bold text-slate-900">{item.title}</h4>
                                            <p className="text-slate-600">{item.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="relative">
                            <div className="aspect-square bg-slate-100 rounded-3xl overflow-hidden border border-slate-200 flex items-center justify-center p-8">
                                <div className="w-full space-y-4">
                                    <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 animate-pulse">
                                        <div className="h-4 w-3/4 bg-slate-100 rounded mb-2"></div>
                                        <div className="h-4 w-1/2 bg-slate-50 rounded"></div>
                                    </div>
                                    <div className="bg-indigo-600 p-4 rounded-xl shadow-lg border border-indigo-700 ml-8 transform translate-y-2">
                                        <div className="h-4 w-5/6 bg-white/20 rounded mb-2"></div>
                                        <div className="h-4 w-2/3 bg-white/20 rounded"></div>
                                    </div>
                                    <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 animate-pulse delay-75">
                                        <div className="h-4 w-2/3 bg-slate-100 rounded mb-2"></div>
                                        <div className="h-4 w-1/4 bg-slate-50 rounded"></div>
                                    </div>
                                </div>
                            </div>
                            {/* Floating badge */}
                            <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-xl border border-slate-100 hidden md:block">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center">
                                        <ShieldCheck />
                                    </div>
                                    <div>
                                        <p className="text-sm font-bold text-slate-900">Control First</p>
                                        <p className="text-xs text-slate-500">Audit every interaction</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Product Sections */}
            <section className="py-24 space-y-24">
                {/* Assistant */}
                <div id="assistant" className="max-w-7xl mx-auto px-4">
                    <div className="bg-indigo-900 rounded-[3rem] p-8 md:p-16 text-white overflow-hidden relative">
                        <div className="relative z-10 max-w-2xl">
                            <div className="flex items-center gap-2 mb-6">
                                <Users className="text-indigo-300" />
                                <span className="uppercase tracking-widest text-sm font-bold text-indigo-300">Woddy Assistant</span>
                            </div>
                            <h2 className="text-4xl md:text-5xl font-bold mb-6">Designed for operations, not chaos.</h2>
                            <p className="text-lg text-indigo-100/80 mb-8 leading-relaxed">
                                Help teams prepare, respond, and stay aligned-safely. The Assistant is a work buddy that reduces dependency on individuals while keeping work moving.
                            </p>

                            <div className="grid sm:grid-cols-2 gap-6 mb-10">
                                <div className="space-y-4">
                                    <h4 className="font-bold text-xl text-white">Capabilities</h4>
                                    <ul className="space-y-3 text-indigo-100/70">
                                        <li className="flex items-center gap-2">
                                            <ChevronRight size={16} className="text-indigo-400" /> Fast answers from context
                                        </li>
                                        <li className="flex items-center gap-2">
                                            <ChevronRight size={16} className="text-indigo-400" /> Drafts for human review
                                        </li>
                                        <li className="flex items-center gap-2">
                                            <ChevronRight size={16} className="text-indigo-400" /> Reduce single-point failure
                                        </li>
                                    </ul>
                                </div>
                                <div className="space-y-4">
                                    <h4 className="font-bold text-xl text-white">Control Center</h4>
                                    <ul className="space-y-3 text-indigo-100/70">
                                        <li className="flex items-center gap-2">
                                            <ChevronRight size={16} className="text-indigo-400" /> You decide permissions
                                        </li>
                                        <li className="flex items-center gap-2">
                                            <ChevronRight size={16} className="text-indigo-400" /> You define data sources
                                        </li>
                                        <li className="flex items-center gap-2">
                                            <ChevronRight size={16} className="text-indigo-400" /> Audit trail for everything
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <button
                                onClick={() => scrollToSection('assistant')}
                                className="bg-white text-indigo-900 px-8 py-4 rounded-full font-bold hover:bg-indigo-50 transition-colors"
                            >
                                Talk to Us
                            </button>
                        </div>

                        {/* Visual element */}
                        <div className="absolute top-0 right-0 w-1/3 h-full bg-indigo-800/50 hidden lg:flex items-center justify-center">
                            <div className="w-64 h-80 bg-white/10 rounded-2xl border border-white/20 backdrop-blur-sm p-6 transform translate-x-12 rotate-3">
                                <div className="h-4 w-12 bg-indigo-400 rounded mb-8"></div>
                                <div className="space-y-4">
                                    <div className="h-2 w-full bg-white/20 rounded"></div>
                                    <div className="h-2 w-full bg-white/20 rounded"></div>
                                    <div className="h-2 w-2/3 bg-white/20 rounded"></div>
                                </div>
                                <div className="mt-12 pt-8 border-t border-white/10">
                                    <div className="h-8 w-24 bg-indigo-500 rounded-lg"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Coach */}
                <div id="coach" className="max-w-7xl mx-auto px-4">
                    <div className="bg-emerald-50 rounded-[3rem] p-8 md:p-16 border border-emerald-100">
                        <div className="flex flex-col lg:flex-row gap-16 items-center">
                            <div className="lg:w-1/2 order-2 lg:order-1">
                                <div className="bg-white p-8 rounded-3xl shadow-xl border border-emerald-100 max-w-sm mx-auto transform -rotate-2">
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center text-white">
                                            <Target size={20} />
                                        </div>
                                        <div>
                                            <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">Woddy Coach</p>
                                            <p className="font-bold text-slate-900">Weekly Reflection</p>
                                        </div>
                                    </div>
                                    <div className="space-y-4">
                                        <p className="text-sm italic text-slate-500">"Based on your work on the Q3 project, you demonstrated strong stakeholder management..."</p>
                                        <div className="p-3 bg-emerald-50 rounded-xl text-xs font-medium text-emerald-700">
                                            View growth areas →
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="lg:w-1/2 order-1 lg:order-2">
                                <div className="flex items-center gap-2 mb-6">
                                    <Target className="text-emerald-600" />
                                    <span className="uppercase tracking-widest text-sm font-bold text-emerald-600">Woddy Coach</span>
                                </div>
                                <h2 className="text-4xl md:text-5xl font-bold mb-6 text-slate-900 leading-tight">Growth grounded in what you actually did.</h2>
                                <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                                    The Coach is not generic advice. It understands your specific work context-last week, last quarter, last year-to help you reflect, prepare, and progress.
                                </p>
                                <div className="space-y-4 mb-10">
                                    <div className="flex items-start gap-3">
                                        <div className="mt-1 bg-emerald-200 rounded-full p-1"><ChevronRight size={14} className="text-emerald-700" /></div>
                                        <p className="text-slate-700 font-medium">Prepare for standups, 1:1s, and reviews</p>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <div className="mt-1 bg-emerald-200 rounded-full p-1"><ChevronRight size={14} className="text-emerald-700" /></div>
                                        <p className="text-slate-700 font-medium">Identify strengths and internal opportunities</p>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <div className="mt-1 bg-emerald-200 rounded-full p-1"><ChevronRight size={14} className="text-emerald-700" /></div>
                                        <p className="text-slate-700 font-medium">Relevant, grounded career retention</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => scrollToSection('coach')}
                                    className="bg-emerald-600 text-white px-8 py-4 rounded-full font-bold hover:bg-emerald-700 transition-colors"
                                >
                                    Talk to Us
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Differentiator Section */}
            <section className="py-24 bg-slate-900 text-white overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-3xl md:text-5xl font-bold mb-16">The Woddy Philosophy</h2>
                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="p-8 rounded-3xl bg-slate-800/50 border border-slate-700">
                            <div className="text-indigo-400 mb-4 text-sm font-bold uppercase tracking-widest">The Speed Moat</div>
                            <h3 className="text-2xl font-bold mb-4 text-white">Speed with Control</h3>
                            <p className="text-slate-400">Other tools focus on automation without boundaries. Woddy is intentionally designed not to be always on.</p>
                        </div>
                        <div className="p-8 rounded-3xl bg-slate-800/50 border border-slate-700">
                            <div className="text-emerald-400 mb-4 text-sm font-bold uppercase tracking-widest">The Knowledge Moat</div>
                            <h3 className="text-2xl font-bold mb-4 text-white">Knowledge that Stays</h3>
                            <p className="text-slate-400">Context stays accessible even when roles change. Retain operational intelligence, not just data.</p>
                        </div>
                        <div className="p-8 rounded-3xl bg-slate-800/50 border border-slate-700">
                            <div className="text-amber-400 mb-4 text-sm font-bold uppercase tracking-widest">The Growth Moat</div>
                            <h3 className="text-2xl font-bold mb-4 text-white">Growth with Context</h3>
                            <p className="text-slate-400">Advice without context is noise. The Coach uses your real work history to drive meaningful progression.</p>
                        </div>
                    </div>

                    <div className="mt-20 pt-20 border-t border-slate-800">
                        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
                            <div className="text-left">
                                <h3 className="text-2xl font-bold mb-2">Ready to move faster?</h3>
                                <p className="text-slate-400">Start with the help you need today. Woddy grows with you.</p>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
                                <button onClick={openModal} className="bg-transparent border-2 border-slate-700 text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-slate-800 transition-colors">
                                    Talk to Us
                                </button>
                            </div>
                        </div>
                        <div className="mt-8 text-sm text-slate-500 flex flex-wrap justify-center gap-6">
                            <span>No forced platform</span>
                            <span>No uncontrolled automation</span>
                            <span>Built for real work</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 bg-white border-t border-slate-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-8">
                        <div className="flex items-center gap-2">
                            <WoddyLogo className="w-8 h-8" />
                            <span className="text-xl font-bold tracking-tight text-slate-900">WODDY</span>
                        </div>
                        <div className="flex gap-8 text-sm font-medium text-slate-500">
                            <a href="https://www.linkedin.com/company/woddy-ai" target="_blank" rel="noopener noreferrer" className="hover:text-slate-900">LinkedIn</a>
                        </div>
                        <div className="text-sm text-slate-400">
                            © 2026 Woddy.ai. All rights reserved.
                        </div>
                    </div>
                    <div className="mt-8 pt-8 border-t border-slate-100 text-center text-xs text-slate-400 max-w-2xl mx-auto">
                        Woddy is a work buddy that helps teams move faster, keep knowledge when people move on, and helps people grow through real work context - with control.
                    </div>
                </div>
            </footer>

            {/* Modal */}
            {isModalOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                    onClick={closeModal}
                >
                    <div
                        className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 md:p-10 relative animate-fade-in-scale"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={closeModal}
                            className="absolute top-6 right-6 text-slate-400 hover:text-slate-600 transition-colors"
                            aria-label="Close modal"
                        >
                            <X size={24} />
                        </button>

                        {formState === 'success' ? (
                            <div className="text-center">
                                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <ShieldCheck className="text-emerald-600" size={32} />
                                </div>
                                <h3 className="text-2xl font-bold text-slate-900 mb-4">
                                    Thanks! You're on the list.
                                </h3>
                                <p className="text-slate-600 mb-6">
                                    We'll be in touch soon with early access details.
                                </p>
                                <button
                                    onClick={closeModal}
                                    className="bg-slate-900 text-white px-6 py-3 rounded-full font-semibold hover:bg-slate-800 transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        ) : (
                            <>
                                <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                                    Get Started with Woddy
                                </h3>
                                <p className="text-slate-600 mb-8">
                                    Join the early access list and be among the first to experience Woddy.
                                </p>

                                <form
                                    name="early-access"
                                    method="POST"
                                    data-netlify="true"
                                    netlify-honeypot="bot-field"
                                    onSubmit={handleSubmit}
                                    className="space-y-4"
                                    noValidate
                                >
                                    <input type="hidden" name="form-name" value="early-access" />
                                    <p hidden>
                                        <label>
                                            Don't fill this out: <input name="bot-field" />
                                        </label>
                                    </p>

                                    <div>
                                        <label htmlFor="modal-email" className="sr-only">
                                            Email address
                                        </label>
                                        <input
                                            type="email"
                                            id="modal-email"
                                            name="email"
                                            value={email}
                                            onChange={handleEmailChange}
                                            placeholder="Enter your email..."
                                            required
                                            aria-required="true"
                                            aria-invalid={formState === 'error' ? 'true' : 'false'}
                                            aria-describedby={formState === 'error' ? 'modal-email-error' : undefined}
                                            disabled={formState === 'submitting'}
                                            className="w-full px-4 py-3 text-base rounded-xl border-2 border-slate-200 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        />
                                    </div>

                                    {formState === 'error' && (
                                        <div
                                            id="modal-email-error"
                                            role="alert"
                                            aria-live="assertive"
                                            className="text-sm text-red-600"
                                        >
                                            {errorMessage}
                                        </div>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={formState === 'submitting'}
                                        className="w-full bg-slate-900 text-white px-6 py-3 rounded-full font-bold text-lg hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                                    >
                                        {formState === 'submitting' ? (
                                            <>
                                                <svg
                                                    className="animate-spin h-5 w-5 text-white"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    aria-hidden="true"
                                                >
                                                    <circle
                                                        className="opacity-25"
                                                        cx="12"
                                                        cy="12"
                                                        r="10"
                                                        stroke="currentColor"
                                                        strokeWidth="4"
                                                    />
                                                    <path
                                                        className="opacity-75"
                                                        fill="currentColor"
                                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                    />
                                                </svg>
                                                Submitting...
                                            </>
                                        ) : (
                                            'Get Early Access'
                                        )}
                                    </button>

                                    <p className="text-xs text-slate-500 text-center">
                                        You'll only hear from us when it matters - no spam
                                    </p>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default App;